import { ReactNode, useState } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { AccessSource } from '@prisma/client';
import { Empty, FormItemProps, Modal, TableProps, Tour, TourStepProps, Typography } from 'antd';
import { FormInstance } from 'antd/es/form';

import { AgsEntitlementMeta, AgsRoleMeta } from '@app/domain-access-interfaces';
import { BundleAccessWithChildrenResult, ValidateAccessEligibilityResult } from '@app/domain-bundle-interfaces';
import { SelectTable } from '@app/web-components';

import { EmployeeTypeRenderer } from '../../../common';
import { useSelectAccessValidations } from '../rules';

export type SelecAccessFormItemProps<T> = {
  props?: TableProps<T>;
  form?: FormInstance;
  selectAccessTourStepProps?: SelectAccessTourStepProps;
};

export type SelectAccessTooltipProps = {
  paragraphs?: ReactNode;
};

export type SelectAccessTourStepProps = {
  steps?: TourStepProps[];
  title?: string;
};

export const SelectAccessTooltipText: React.FC<SelectAccessTooltipProps> = ({ paragraphs }) => (
  <>
    {paragraphs}
    <p>
      At least one access must be selected. If you add an access with dependencies, you will be asked to add the
      dependencies or cancel. If you remove an access with dependents, you will be asked to remove the dependents or
      cancel.
    </p>
  </>
);

export const useSelectAccessesFormItem = <T extends ValidateAccessEligibilityResult | BundleAccessWithChildrenResult>({
  props,
  form,
  selectAccessTourStepProps,
}: SelecAccessFormItemProps<T>) => {
  const [open, setOpen] = useState(false);
  const { confirm } = Modal;

  const steps: TourStepProps[] = [...(selectAccessTourStepProps?.steps ?? [])];

  const { accessDependenciesRule, getSelectedDependents, getMissingDependencies, onCancelSelect } =
    useSelectAccessValidations({
      data: [...(props?.dataSource ?? [])],
      form,
    });

  const getProps = (): FormItemProps => ({
    children: (
      <SelectTable
        columns={[
          ...(props?.columns ?? []),
          {
            title: 'Name',
            dataIndex: 'accessName',
            key: 'accessName',
            render: (_, record) => {
              if (record.accessSource === AccessSource.AGS_ROLE) {
                const meta = record.meta as AgsRoleMeta;
                return (
                  <>
                    <div>{record.accessName}</div>
                    {record.accessName && meta && meta.displayName && record.accessName !== meta.displayName && (
                      <Typography.Text type="secondary">a.k.a. {meta.displayName}</Typography.Text>
                    )}
                  </>
                );
              }

              if (record.accessSource === AccessSource.AGS_ENTITLEMENT) {
                const meta = record.meta as AgsEntitlementMeta;
                return (
                  <>
                    <div>{record.accessName}</div>
                    {record.accessName && meta && meta.displayName && record.accessName !== meta.displayName && (
                      <Typography.Text type="secondary">a.k.a. {meta.displayName}</Typography.Text>
                    )}
                  </>
                );
              }

              return record.accessName;
            },
          },
          {
            title: 'Source',
            dataIndex: 'accessSource',
            width: 120,
            render: (_, record) => record.accessSource,
          },
          {
            title: 'Emp Type',
            dataIndex: 'employeeType',
            width: 120,
            render: (_, record) => <EmployeeTypeRenderer employeeType={record.accessEmployeeType} />,
          },
        ]}
        dataSource={props?.dataSource ?? []}
        loading={props?.loading}
        pagination={false}
        rowKey="bundleAccessId"
        rowSelection={{
          ...props?.rowSelection,
          onSelect: (record, selected, selectedRows, e) => {
            if (selected) {
              const dependencies: T[] = [];
              getMissingDependencies(record, dependencies);
              const inEligible = dependencies.find((x) => 'isEligible' in x && !x.isEligible);

              if (inEligible) {
                confirm({
                  title: `The access you wish to add has a dependency on ${inEligible.accessName}, which the selected worker is not eligible for.`,
                  cancelButtonProps: { style: { display: 'none' } },
                  onOk: () => onCancelSelect(record, selected, selectedRows, e),
                });
              } else if (dependencies.length) {
                confirm({
                  title: 'The access you wish to add has dependencies. Would you like to add them?',
                  content: (
                    <>
                      You will be adding the following accesses:{' '}
                      <ul>
                        {dependencies.map((x) => (
                          <li key={x.bundleAccessId}>{x.accessName}</li>
                        ))}
                      </ul>
                    </>
                  ),
                  onCancel: () => onCancelSelect(record, selected, selectedRows, e),
                  onOk: () => {
                    form?.setFieldValue('bundleAccessIds', [
                      ...selectedRows.map((x) => x.bundleAccessId),
                      ...dependencies.map((x) => x.bundleAccessId),
                    ]);
                    form?.validateFields();
                  },
                });
              }
            } else {
              const dependents: T[] = [];
              getSelectedDependents(record, dependents);

              if (dependents.length) {
                confirm({
                  title: 'The access you wish to remove has accesses dependent on it. Would you like to remove them?',
                  content: (
                    <>
                      You will be removing the following accesses:{' '}
                      <ul>
                        {dependents.map((x) => (
                          <li key={x.bundleAccessId}>{x.accessName}</li>
                        ))}
                      </ul>
                    </>
                  ),
                  onCancel: () => onCancelSelect(record, selected, selectedRows, e),
                  onOk: () => {
                    form?.setFieldValue(
                      'bundleAccessIds',
                      selectedRows
                        .filter((x) => !dependents.find((y) => y.bundleAccessId === x.bundleAccessId))
                        .map((x) => x.bundleAccessId),
                    );
                    form?.validateFields();
                  },
                });
              }
            }
          },
        }}
        renderEmpty={
          props?.dataSource
            ? undefined
            : () => (
                <Empty
                  description="Select a worker and a bundle to view available accesses."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
        }
      />
    ),
    label: 'Select Accesses',
    name: 'bundleAccessIds',
    rules: [
      { required: true, message: 'Must select at least one access.' },
      () => ({
        validator: accessDependenciesRule(),
      }),
    ],
    tooltip: {
      icon: (
        <span>
          <QuestionCircleOutlined onClick={() => setOpen(true)} />
          <Tour open={open} steps={steps} onClose={() => setOpen(false)} onFinish={() => setOpen(false)} />
        </span>
      ),
      title: selectAccessTourStepProps?.title ?? 'Select Accesses',
    },
  });

  return { getProps };
};
